import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  useGetDashboardStatisticsQuery,
  useGetUpcomingEmployeeEventsQuery,
  useGetRecentActivityQuery,
} from 'redux/features/admin/adminApi';

import dayjs from 'lib/dayjs';
import {
  useApproveEmployeeLeaveMutation,
  useRejectEmployeeLeaveMutation,
  useAddEmployeeLeaveMutation,
  useGetUpcomingLeavesQuery,
  useGetTodayUnplannedLeavesQuery,
} from 'redux/features/employeeLeaveApi';
import { toast } from 'react-toastify';
import { getSocket } from 'lib/socket';
import { matchesNameSearch } from 'utils/common';
import { useGetUsersQuery } from 'redux/features/users/userApi';
import EventCard from 'components/ui/EventCard';
import { useGetSettingsQuery } from 'redux/features/settings/settingsApi';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import CommandPalette from '../../components/ui/CommandPalette';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import SkeletonWrapper from '../../components/ui/SkeletonWrapper';
import LeaveCard from '../../components/ui/LeaveCard';
import LunchBreakTrackerList from '../../components/ui/LunchBreakTrackerList';
import RejectLeaveModal from '../admin-leave-management/components/RejectLeaveModal';
import AddLeaveModal from '../holiday-management-employee-view/components/AddLeaveModal';
import LeaveDetailsModal from '../admin-leave-management/components/LeaveDetailsModal';
import KPICard from './components/KPICard';
import UnplannedLeaveCard from './components/UnplannedLeaveCard';
import ActivityItem from './components/ActivityItem';

const actionTypeMap = {
  CHECK_IN: 'attendance',
  CHECK_OUT: 'checkout',
  CREATE_LEAVE: 'leave',
  APPROVE_LEAVE: 'leave',
  REJECT_LEAVE: 'reject',
  UPDATE_LEAVE: 'leave',
  APPROVE_LOG_UPDATE: 'system',
  CREATE_LOG_UPDATE_REQUEST: 'system',
};

const mapActionToType = (action) => actionTypeMap[action] || 'system';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [removedUnplannedIds, setRemovedUnplannedIds] = useState([]);
  const [activities, setActivities] = useState([]);
  const [activityPage, setActivityPage] = useState(1);
  const [hasMoreActivity, setHasMoreActivity] = useState(true);
  const [searchActivityInput, setSearchActivityInput] = useState('');
  const [searchActivity, setSearchActivity] = useState('');

  const {
    data: statsData,
    isLoading: isLoadingStats,
    refetch: refetchStats,
  } = useGetDashboardStatisticsQuery();

  const {
    data: activityData,
    isLoading: isLoadingActivity,
    isFetching: isFetchingActivity,
  } = useGetRecentActivityQuery({ page: activityPage, limit: 10, search: searchActivity });
  const { data: upcomingLeaves, isLoading: isLoadingUpcomingLeaves } = useGetUpcomingLeavesQuery();

  const {
    data: unplannedLeavesToday,
    isLoading: isLoadingUnplanned,
    refetch: refetchTodayUnplanned,
  } = useGetTodayUnplannedLeavesQuery();

  const [addEmployeeLeave] = useAddEmployeeLeaveMutation();
  const { data: employees } = useGetUsersQuery();

  // Add leave on behalf of employee
  const [isAddLeaveModalOpen, setIsAddLeaveModalOpen] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [selectedLeaveDate, setSelectedLeaveDate] = useState(null);
  const [selectedLeaveType, setSelectedLeaveType] = useState('');
  const [unplannedLeaveToAdd, setUnplannedLeaveToAdd] = useState(null);

  const [searchPending, setSearchPending] = useState('');
  const [searchOngoing, setSearchOngoing] = useState('');
  const [searchConfirmed, setSearchConfirmed] = useState('');
  const [searchUnplanned, setSearchUnplanned] = useState('');

  const [approveLeave, { isLoading: isApproving }] = useApproveEmployeeLeaveMutation();
  const [rejectLeave, { isLoading: isRejecting }] = useRejectEmployeeLeaveMutation();

  const today = dayjs();

  const [selectedPeriod, setSelectedPeriod] = useState('next30');
  const [isEventDropdownOpen, setIsEventDropdownOpen] = useState(false);

  const monthOptions = [
    { value: 'next30', label: 'Next 30 days' },
    ...Array.from({ length: 12 }, (_, i) => {
      const m = today.add(i, 'month');
      return { value: `${m.month() + 1}-${m.year()}`, label: m.format('MMMM YYYY') };
    }),
  ];

  const selectedLabel =
    monthOptions.find((o) => o.value === selectedPeriod)?.label || 'Next 30 days';

  const queryArgs =
    selectedPeriod === 'next30'
      ? {}
      : {
          month: Number(selectedPeriod.split('-')[0]),
          year: Number(selectedPeriod.split('-')[1]),
        };

  const { data: upcomingEventsData, isLoading: isLoadingEvents } =
    useGetUpcomingEmployeeEventsQuery(queryArgs);

  const upcomingBirthdays = upcomingEventsData?.data?.upcoming_birthdays || [];
  const upcomingWorkAnniversaries = upcomingEventsData?.data?.upcoming_anniversaries || [];
  const upcomingHolidays = upcomingEventsData?.data?.holidays || [];
  const upcomingBondExpiries = upcomingEventsData?.data?.employee_bond || [];

  const combinedEvents = [
    ...upcomingBirthdays.map((event) => ({
      ...event,
      eventType: 'birthday',
      sortDate: event.next_birthday,
    })),
    ...upcomingWorkAnniversaries.map((event) => ({
      ...event,
      eventType: 'anniversary',
      sortDate: event.next_anniversary,
    })),
    ...upcomingHolidays.map((event) => ({
      ...event,
      eventType: 'holiday',
      sortDate: event.date,
    })),
    ...upcomingBondExpiries.map((event) => ({
      ...event,
      eventType: 'bond_expiry',
      sortDate: event.end_date,
    })),
  ].sort((a, b) => dayjs(a.sortDate).valueOf() - dayjs(b.sortDate).valueOf());

  const groupedEvents = combinedEvents.reduce((groups, event) => {
    const dateKey = dayjs(event.sortDate).format('YYYY-MM-DD');
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(event);
    return groups;
  }, {});

  const sortedDates = Object.keys(groupedEvents).sort(
    (a, b) => dayjs(a).valueOf() - dayjs(b).valueOf(),
  );

  const socket = getSocket();
  useEffect(() => {
    if (socket) {
      const handleRecentActivity = (data) => {
        const newActivity = {
          id: data.id,
          actor_name: data.user
            ? `${data.user.first_name} ${data.user.last_name}`
            : data.admin?.name || 'System',
          action: data.action_type,
          target: data.details || '',
          created_at: data.created_at,
          time: dayjs(data.created_at).format('MMM D, h:mm A'),
          type: mapActionToType(data.action_type),
        };

        setActivities((prev) => {
          if (prev.some((item) => item.id === newActivity.id)) {
            return prev;
          }

          // Always place newest socket item at the top
          return [newActivity, ...prev];
        });
      };
      socket.on('recentActivity', handleRecentActivity);
      return () => {
        socket.off('recentActivity', handleRecentActivity);
      };
    }
  }, [socket]);

  useEffect(() => {
    setActivities([]);
    setActivityPage(1);
    setHasMoreActivity(true);
  }, [searchActivity]);

  useEffect(() => {
    if (activityData?.data?.data) {
      setActivities((prev) => {
        const existingIds = new Set(prev.map((a) => a.id));

        const newItems = activityData.data.data
          .filter((item) => !existingIds.has(item.id))
          .map((item) => ({
            id: item.id,
            actor_name: item.user
              ? `${item.user.first_name} ${item.user.last_name}`
              : item.admin?.name || 'System',
            action: item.action_type,
            target: item.details || '',
            created_at: item.created_at,
            time: dayjs(item.created_at).format('MMM D, YYYY, h:mm A'),
            type: mapActionToType(item.action_type),
          }));

        if (activityPage === 1) {
          // Initial load
          return [...newItems, ...prev].slice(0, 10);
        }

        // Infinite scroll
        return [...prev, ...newItems];
      });

      setHasMoreActivity(activityData.data.total > activityPage * 10);
    }
  }, [activityData, activityPage]);

  useEffect(() => {
    if (searchActivityInput === '') {
      setSearchActivity('');
      return;
    }
    const timer = setTimeout(() => {
      setSearchActivity(searchActivityInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchActivityInput]);

  const [rejectLeaveModalData, setRejectLeaveModalData] = useState(null);
  const [approveLeaveModalData, setApproveLeaveModalData] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);

  const { data: settingsData } = useGetSettingsQuery();
  const workSchedule = {
    dayStartTime: settingsData?.data?.day_start_time,
    dayEndTime: settingsData?.data?.day_end_time,
    breakResumeTime: settingsData?.data?.break_start_time,
  };

  const handleCardClick = (leave) => {
    setSelectedLeave(leave);
    setDetailsModalOpen(true);
  };

  const handleApproveClick = (leave) => {
    setApproveLeaveModalData(leave);
  };

  const handleConfirmApprove = async () => {
    if (!approveLeaveModalData) return;
    const leaveId = approveLeaveModalData.id;
    setProcessingId(leaveId);
    setApproveLeaveModalData(null);
    try {
      await approveLeave(leaveId).unwrap();
      toast.success('Leave approved successfully');
    } catch (error) {
      console.error('Failed to approve leave:', error);
    } finally {
      setProcessingId(null);
    }
  };

  const handleRejectClick = (leave) => {
    setRejectLeaveModalData(leave);
  };

  const handleConfirmReject = async (reason) => {
    if (!rejectLeaveModalData) return;
    setProcessingId(rejectLeaveModalData.id);
    try {
      await rejectLeave({ id: rejectLeaveModalData.id, reason }).unwrap();
      toast.success('Leave rejected successfully');
      setRejectLeaveModalData(null);
    } catch (error) {
      console.error('Failed to reject leave:', error);
    } finally {
      setProcessingId(null);
    }
  };

  const handleAddLeaveOnBehalf = async (leaveData) => {
    try {
      const payload = { ...leaveData, status: 'APPROVED' };
      await addEmployeeLeave(payload).unwrap();
      toast.success('Leave added on behalf of employee successfully');
      if (unplannedLeaveToAdd) {
        setRemovedUnplannedIds((prev) => [...prev, unplannedLeaveToAdd.id]);
        setUnplannedLeaveToAdd(null);
      }
      refetchTodayUnplanned();
      refetchStats();
    } catch (error) {
      console.error('Failed to add leave:', error);
      toast.error(error?.data?.message || 'Failed to add leave');
    }
  };

  const ongoingLeaves =
    upcomingLeaves?.filter(
      (leave) =>
        leave.status === 'APPROVED' &&
        dayjs(leave.start_date_time).isBefore(dayjs().endOf('day')) &&
        dayjs(leave.end_date_time).isAfter(dayjs().startOf('day')),
    ) || [];

  // unplannedLeavesToday is now directly provided by the query

  const confirmedUpcomingLeaves =
    upcomingLeaves?.filter(
      (leave) =>
        leave.status === 'APPROVED' && dayjs(leave.start_date_time).isAfter(dayjs().endOf('day')),
    ) || [];

  const pendingUpcomingLeaves = upcomingLeaves?.filter((leave) => leave.status === 'PENDING') || [];

  const filterBySearch = (leaves, query) => {
    if (!query?.trim()) return leaves;
    return leaves.filter((leave) => leave.user && matchesNameSearch(leave.user, query));
  };

  // Build set of user IDs who have any leave (approved/pending) covering today
  // so they are excluded from the unplanned leave list
  const todayLeaveUserIds = new Set(
    (upcomingLeaves || [])
      .filter(
        (leave) =>
          dayjs(leave.start_date_time).isBefore(dayjs().endOf('day')) &&
          dayjs(leave.end_date_time).isAfter(dayjs().startOf('day')),
      )
      .map((leave) => leave.user_id),
  );

  const filteredPending = filterBySearch(pendingUpcomingLeaves, searchPending);
  const filteredOngoing = filterBySearch(ongoingLeaves, searchOngoing);
  const filteredConfirmed = filterBySearch(confirmedUpcomingLeaves, searchConfirmed);
  // Employees with a leave today or already handled are not unplanned, so they are not counted either
  const openUnplannedToday = (unplannedLeavesToday || [])
    .filter((leave) => !removedUnplannedIds.includes(leave.id))
    .filter((leave) => !todayLeaveUserIds.has(leave.user_id));
  const filteredUnplanned = filterBySearch(openUnplannedToday, searchUnplanned);

  const stats = statsData?.data || {
    activeUsers: 0,
    inactiveUsers: 0,
    totalUsers: 0,
    activeSession: 0,
    inactiveSession: 0,
    pendingLeaveRequests: 0,
    pendingUnplannedLeaves: 0,
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const kpiRoutes = {
    'Pending Leaves': '/admin-leave-management',
    'Log Update Requests': '/admin-request-management',
    'Active Sessions': '/employee-management',
    'Total Employees': '/employee-management',
    'Unplanned Leaves': '#unplanned-leaves',
  };

  const kpis = [
    {
      label: 'Pending Leaves',
      value: stats.pendingLeaveRequests,
      icon: 'FileText',
      color: 'purple',
    },
    {
      label: 'Log Update Requests',
      value: stats.pendingLogUpdateRequests || 0,
      icon: 'AlertCircle',
      color: 'pink',
    },
    {
      label: 'Active Sessions',
      value: stats.activeSession,
      icon: 'Zap',
      color: 'green',
    },
    {
      label: 'Total Employees',
      value: stats.totalUsers,
      icon: 'Users',
      color: 'blue',
    },
    {
      label: 'Unplanned Leave Today',
      value: openUnplannedToday.length,
      icon: 'CalendarOff',
      color: 'red',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-background to-secondary-50">
      <Header />
      <Sidebar />
      <CommandPalette />

      <main className="lg:ml-60 pt-16">
        <motion.div
          className="px-4 sm:px-6 lg:px-8 py-4 lg:py-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Dashboard Header */}
          <motion.div className="mb-8" variants={itemVariants}>
            <h1 className="text-3xl font-bold text-text-primary mb-2">Dashboard</h1>
            <p className="text-text-secondary ">
              Overview of organization activities and pending tasks dashboard statistics
            </p>
          </motion.div>

          {/* KPI Cards Grid */}
          <motion.div
            variants={itemVariants}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-3"
          >
            {kpis.map((kpi, index) => (
              <KPICard
                key={index}
                label={kpi.label}
                value={kpi.value}
                icon={kpi.icon}
                color={kpi.color}
                isLoading={isLoadingStats}
                onClick={() => {
                  const route = kpiRoutes[kpi.label];
                  if (route && route.startsWith('/')) {
                    navigate(route);
                  }
                }}
              />
            ))}
          </motion.div>

          <motion.div
            variants={itemVariants}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 mb-8 items-stretch"
          >
            <div className="lg:col-span-1">
              <div className="bg-surface border border-border rounded-xl p-3 sm:p-5 shadow-sm h-full flex flex-col">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg bg-amber-100">
                      <Icon name="Calendar" size={18} className="text-amber-600" />
                    </div>
                    <h3 className="text-sm sm:text-base text-text-primary">
                      Pending Upcoming Leaves
                    </h3>
                  </div>
                  <div className="relative w-full sm:w-auto">
                    <Icon
                      name="Search"
                      size={14}
                      className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-secondary"
                    />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchPending}
                      onChange={(e) => setSearchPending(e.target.value)}
                      className="w-full sm:w-[130px] pl-8 pr-2.5 py-1.5 text-xs border border-border rounded-lg bg-background text-text-primary placeholder-text-secondary focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 -mr-1 max-h-[200px] sm:max-h-[280px] min-h-[200px] sm:min-h-[280px]">
                  {isLoadingUpcomingLeaves ? (
                    <div className="space-y-3">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="p-3 rounded-xl border border-border">
                          <SkeletonWrapper isLoading={true} width="w-full" height="h-10" />
                        </div>
                      ))}
                    </div>
                  ) : filteredPending.length === 0 ? (
                    <div className="py-10 flex flex-col items-center justify-center text-center">
                      <div className="p-3 rounded-full bg-secondary-50 mb-3">
                        <Icon
                          name={searchPending ? 'Search' : 'CalendarOff'}
                          size={24}
                          className="text-text-tertiary"
                        />
                      </div>
                      <h4 className="text-sm font-bold text-text-primary">
                        {searchPending ? 'No matching records' : 'No pending upcoming leaves'}
                      </h4>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredPending.map((leave, index) => (
                        <LeaveCard
                          key={index}
                          leave={leave}
                          isAdmin={true}
                          onApprove={handleApproveClick}
                          onReject={handleRejectClick}
                          isLoading={isApproving || isRejecting}
                          processingId={processingId}
                          onClick={() => handleCardClick(leave)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="bg-surface border border-border rounded-xl p-3 sm:p-5 shadow-sm h-full flex flex-col">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg bg-pink-100">
                      <Icon name="PartyPopper" size={18} className="text-pink-600" />
                    </div>
                    <h3 className="text-sm sm:text-base text-text-primary">Upcoming Events</h3>
                  </div>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setIsEventDropdownOpen(!isEventDropdownOpen)}
                      className="flex items-center gap-1.5 text-xs text-text-primary font-medium bg-background border border-border rounded-lg px-3 py-1.5 hover:bg-secondary-50 focus:outline-none focus:ring-1 focus:ring-primary-500 transition-colors"
                    >
                      <Icon name="Calendar" size={13} className="text-text-secondary" />
                      {selectedLabel}
                      <Icon
                        name="ChevronDown"
                        size={13}
                        className={`text-text-secondary transition-transform duration-200 ${isEventDropdownOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {isEventDropdownOpen && (
                      <>
                        <div
                          className="fixed inset-0 z-40"
                          onClick={() => setIsEventDropdownOpen(false)}
                        />
                        <div className="absolute right-0 z-50 mt-1 w-48 bg-surface border border-border rounded-lg shadow-lg py-1 max-h-[280px] overflow-y-auto animate-in fade-in slide-in-from-top-2 duration-150">
                          {monthOptions.map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => {
                                setSelectedPeriod(opt.value);
                                setIsEventDropdownOpen(false);
                              }}
                              className={`w-full text-left px-3 py-2 text-xs font-medium transition-colors flex items-center justify-between ${
                                selectedPeriod === opt.value
                                  ? 'bg-primary-50 text-primary-700'
                                  : 'text-text-primary hover:bg-secondary-50'
                              }`}
                            >
                              {opt.label}
                              {selectedPeriod === opt.value && (
                                <Icon name="Check" size={14} className="text-primary-600" />
                              )}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 -mr-1 max-h-[200px] sm:max-h-[280px] min-h-[200px] sm:min-h-[280px]">
                  {isLoadingEvents ? (
                    <div className="space-y-3">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="p-3 rounded-xl border border-border">
                          <SkeletonWrapper isLoading={true} width="w-full" height="h-10" />
                        </div>
                      ))}
                    </div>
                  ) : sortedDates.length === 0 ? (
                    <div className="py-10 flex flex-col items-center justify-center text-center">
                      <div className="p-3 rounded-full bg-secondary-50 mb-3">
                        <Icon name="CalendarOff" size={24} className="text-text-tertiary" />
                      </div>
                      <h4 className="text-sm font-bold text-text-primary">
                        {selectedPeriod === 'next30'
                          ? 'No upcoming events in the next 30 days'
                          : `No upcoming events for ${selectedLabel}`}
                      </h4>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {sortedDates.map((dateKey) => {
                        const eventsForDate = groupedEvents[dateKey];
                        const dateObj = dayjs(dateKey);
                        const isToday = dateObj.isSame(today, 'day');
                        const relativeText =
                          eventsForDate[0]?.in || (isToday ? 'Today' : dateObj.format('dddd'));

                        return (
                          <div key={dateKey} className="space-y-2">
                            {/* Date Group Header */}
                            <div className="flex items-center justify-between border-b border-slate-100 pb-1 mt-3">
                              <span className="text-xs font-bold text-slate-800 flex items-center gap-1">
                                <Icon name="Calendar" size={11} className="text-slate-400" />
                                {dateObj.format('MMM DD')}
                              </span>
                              <span className="text-[10px] text-slate-400 font-medium uppercase tracking-tight">
                                {relativeText}
                              </span>
                            </div>

                            {/* Events List */}
                            <div className="space-y-1.5 ">
                              {eventsForDate.map((event, index) => (
                                <EventCard
                                  key={`${event.eventType}-${index}`}
                                  event={event}
                                  eventType={event.eventType}
                                />
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* Recent Activity */}
            <div className="lg:col-span-1">
              <div className="bg-surface border border-border rounded-xl p-3 sm:p-5 shadow-sm h-full flex flex-col">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 mb-4">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg bg-indigo-100">
                      <Icon name="Activity" size={18} className="text-indigo-600" />
                    </div>
                    <h3 className="text-sm sm:text-base text-text-primary">Recent Activity</h3>
                  </div>
                  <div className="relative w-full sm:w-auto sm:ml-auto">
                    <Icon
                      name="Search"
                      size={14}
                      className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-secondary"
                    />
                    <input
                      type="search"
                      placeholder="Search..."
                      value={searchActivityInput}
                      onChange={(e) => setSearchActivityInput(e.target.value)}
                      className="w-full sm:w-[130px] pl-8 pr-2.5 py-1.5 text-xs border border-border rounded-lg bg-background text-text-primary placeholder-text-secondary focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                </div>
                <div
                  className="flex-1 overflow-y-auto custom-scrollbar pr-1 -mr-1 max-h-[200px] sm:max-h-[280px] min-h-[200px] sm:min-h-[280px]"
                  onScroll={(e) => {
                    const el = e.currentTarget;
                    if (
                      el.scrollHeight - el.scrollTop <= el.clientHeight + 20 &&
                      hasMoreActivity &&
                      !isFetchingActivity
                    ) {
                      setActivityPage((p) => p + 1);
                    }
                  }}
                >
                  {isLoadingActivity && activities.length === 0 ? (
                    <div className="space-y-1 py-2">
                      {[1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className="flex items-start gap-3 py-3 border-b border-slate-50 last:border-0"
                        >
                          <SkeletonWrapper
                            isLoading={true}
                            width="w-7 h-7"
                            height=""
                            className="rounded-lg shrink-0"
                          />
                          <div className="flex-1 space-y-2">
                            <SkeletonWrapper isLoading={true} width="w-1/3" height="h-3.5" />
                            <SkeletonWrapper isLoading={true} width="w-2/3" height="h-3" />
                            <SkeletonWrapper isLoading={true} width="w-1/5" height="h-2.5" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : activities.length === 0 ? (
                    <div className="py-10 flex flex-col items-center justify-center text-center">
                      <div className="p-3 rounded-full bg-secondary-50 mb-3">
                        <Icon name="Activity" size={24} className="text-text-tertiary" />
                      </div>
                      <h4 className="text-sm font-bold text-text-primary">No recent activity</h4>
                      <p className="text-xs text-text-secondary mt-1">
                        Activity will appear here in real-time
                      </p>
                    </div>
                  ) : (
                    <div>
                      {activities.map((item, index) => (
                        <ActivityItem key={item.id} item={item} index={index} />
                      ))}
                      {isFetchingActivity && (
                        <div className="flex justify-center py-3">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="lg:col-span-1">
              <div className="bg-surface border border-border rounded-xl p-3 sm:p-5 shadow-sm h-full flex flex-col">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg bg-green-100">
                      <Icon name="Calendar" size={18} className="text-green-600" />
                    </div>
                    <h3 className="text-sm sm:text-base text-text-primary">Employee On Leave</h3>
                  </div>
                  <div className="relative w-full sm:w-auto">
                    <Icon
                      name="Search"
                      size={14}
                      className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-secondary"
                    />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchOngoing}
                      onChange={(e) => setSearchOngoing(e.target.value)}
                      className="w-full sm:w-[130px] pl-8 pr-2.5 py-1.5 text-xs border border-border rounded-lg bg-background text-text-primary placeholder-text-secondary focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 -mr-1 max-h-[200px] sm:max-h-[280px] min-h-[200px] sm:min-h-[280px]">
                  {isLoadingUpcomingLeaves ? (
                    <div className="space-y-3">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="p-3 rounded-xl border border-border">
                          <SkeletonWrapper isLoading={true} width="w-full" height="h-10" />
                        </div>
                      ))}
                    </div>
                  ) : filteredOngoing.length === 0 ? (
                    <div className="py-10 flex flex-col items-center justify-center text-center">
                      <div className="p-3 rounded-full bg-secondary-50 mb-3">
                        <Icon
                          name={searchOngoing ? 'Search' : 'CalendarOff'}
                          size={24}
                          className="text-text-tertiary"
                        />
                      </div>
                      <h4 className="text-sm font-bold text-text-primary">
                        {searchOngoing ? 'No matching records' : 'No employees on leave'}
                      </h4>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredOngoing.map((leave, index) => (
                        <LeaveCard
                          key={index}
                          leave={leave}
                          onClick={() => handleCardClick(leave)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="lg:col-span-1">
              <div className="bg-surface border border-border rounded-xl p-3 sm:p-5 shadow-sm h-full flex flex-col">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg bg-blue-100">
                      <Icon name="Calendar" size={18} className="text-blue-600" />
                    </div>
                    <h3 className="text-sm sm:text-base text-text-primary">
                      Confirmed Upcoming Leaves
                    </h3>
                  </div>
                  <div className="relative w-full sm:w-auto">
                    <Icon
                      name="Search"
                      size={14}
                      className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-secondary"
                    />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchConfirmed}
                      onChange={(e) => setSearchConfirmed(e.target.value)}
                      className="w-full sm:w-[130px] pl-8 pr-2.5 py-1.5 text-xs border border-border rounded-lg bg-background text-text-primary placeholder-text-secondary focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 -mr-1 max-h-[200px] sm:max-h-[280px] min-h-[200px] sm:min-h-[280px]">
                  {isLoadingUpcomingLeaves ? (
                    <div className="space-y-3">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="p-3 rounded-xl border border-border">
                          <SkeletonWrapper isLoading={true} width="w-full" height="h-10" />
                        </div>
                      ))}
                    </div>
                  ) : filteredConfirmed.length === 0 ? (
                    <div className="py-10 flex flex-col items-center justify-center text-center">
                      <div className="p-3 rounded-full bg-secondary-50 mb-3">
                        <Icon
                          name={searchConfirmed ? 'Search' : 'CalendarOff'}
                          size={24}
                          className="text-text-tertiary"
                        />
                      </div>
                      <h4 className="text-sm font-bold text-text-primary">
                        {searchConfirmed ? 'No matching records' : 'No confirmed upcoming leaves'}
                      </h4>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredConfirmed.map((leave, index) => (
                        <LeaveCard
                          key={index}
                          leave={leave}
                          isAdmin={true}
                          isLoading={isApproving || isRejecting}
                          processingId={processingId}
                          onClick={() => handleCardClick(leave)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="bg-surface border border-border rounded-xl p-3 sm:p-5 shadow-sm flex flex-col lg:col-span-1">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg bg-red-100">
                      <Icon name="CalendarOff" size={18} className="text-red-600" />
                    </div>
                    <h3 className="text-sm sm:text-base text-text-primary">
                      Unplanned Leave Today
                    </h3>
                    <span className="inline-flex min-w-[22px] items-center justify-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
                      {openUnplannedToday.length}
                    </span>
                  </div>
                  <div className="relative w-full sm:w-auto">
                    <Icon
                      name="Search"
                      size={14}
                      className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-secondary"
                    />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchUnplanned}
                      onChange={(e) => setSearchUnplanned(e.target.value)}
                      className="w-full sm:w-[130px] pl-8 pr-2.5 py-1.5 text-xs border border-border rounded-lg bg-background text-text-primary placeholder-text-secondary focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 -mr-1 max-h-[200px] sm:max-h-[280px] min-h-[200px] sm:min-h-[280px]">
                  {isLoadingUnplanned ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                  ) : !unplannedLeavesToday || unplannedLeavesToday.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64 text-text-secondary">
                      <Icon name="FileText" size={48} className="mb-4 opacity-20" />
                      <p className="text-lg font-medium">No unplanned leaves found</p>
                    </div>
                  ) : filteredUnplanned.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64 text-text-secondary">
                      <Icon name="Search" size={48} className="mb-4 opacity-20" />
                      <p className="text-lg font-medium">No matching records</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredUnplanned.map((leave, index) => (
                        <UnplannedLeaveCard key={leave.id || index} leave={leave} />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Lunch Break Tracker Section */}
          <motion.div
            variants={itemVariants}
            className="mb-8 grid grid-cols-1 lg:grid-cols-1 gap-6"
          >
            <div className="bg-surface border border-border rounded-xl p-3 sm:p-5 shadow-sm h-full flex flex-col lg:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-1.5 rounded-lg bg-orange-100">
                  <Icon name="UtensilsCrossed" size={18} className="text-orange-600" />
                </div>
                <h3 className="text-sm sm:text-base text-text-primary">
                  Employees Who Worked During Lunch Break
                </h3>
              </div>
              <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 -mr-1 max-h-[250px] sm:max-h-[400px] min-h-[250px] sm:min-h-[400px]">
                <LunchBreakTrackerList isAdmin={true} groupByEmployee={true} />
              </div>
            </div>
          </motion.div>
        </motion.div>
        <RejectLeaveModal
          isOpen={!!rejectLeaveModalData}
          onClose={() => setRejectLeaveModalData(null)}
          onConfirm={handleConfirmReject}
          leaveDetails={rejectLeaveModalData}
          type="reject"
        />
        <ConfirmationModal
          isOpen={!!approveLeaveModalData}
          onClose={() => setApproveLeaveModalData(null)}
          onConfirm={handleConfirmApprove}
          title="Approve Leave Request"
          message={`Are you sure you want to approve the leave request for ${approveLeaveModalData?.user?.first_name} ${approveLeaveModalData?.user?.last_name}?`}
          confirmLabel="Approve"
          cancelLabel="Cancel"
          type="success"
        />
        <LeaveDetailsModal
          isOpen={detailsModalOpen}
          onClose={() => {
            setDetailsModalOpen(false);
            setSelectedLeave(null);
          }}
          leave={selectedLeave}
          onApprove={(leave) => {
            setDetailsModalOpen(false);
            handleApproveClick(leave);
          }}
          onReject={(leave) => {
            setDetailsModalOpen(false);
            handleRejectClick(leave);
          }}
          workSchedule={workSchedule}
        />

        {/* Add Leave on Behalf Modal */}
        {isAddLeaveModalOpen && (
          <AddLeaveModal
            isOpen={isAddLeaveModalOpen}
            onClose={() => {
              setIsAddLeaveModalOpen(false);
              setSelectedEmployeeId('');
              setSelectedLeaveDate(null);
              setSelectedLeaveType('');
              setUnplannedLeaveToAdd(null);
            }}
            onAddLeave={handleAddLeaveOnBehalf}
            isAdminMode={true}
            selectedEmployeeId={selectedEmployeeId}
            selectedDate={selectedLeaveDate}
            defaultLeaveType={selectedLeaveType || 'FULL_DAY_LEAVE'}
            employees={employees?.data?.users || []}
          />
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
